import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";

const TropesSelector = ({ selectedThemes = [], selectedTropes = [], onThemesChange, onTropesChange, genre = "fiction" }) => {
  const [activeTab, setActiveTab] = useState("themes");
  
  const isNonfiction = genre?.toLowerCase() === 'non-fiction' || genre?.toLowerCase() === 'nonfiction';

  // Fiction Themes
  const fictionThemeCategories = {
    "Core Themes": [
      "Love", "Revenge", "Redemption", "Power", "Justice", 
      "Freedom", "Sacrifice", "Loyalty", "Betrayal", "Survival"
    ],
    "Emotional Themes": [
      "Coming of Age", "Loss & Grief", "Hope", "Fear", 
      "Identity", "Belonging", "Isolation", "Forgiveness"
    ],
    "Social Themes": [
      "Class Struggle", "Corruption", "Revolution", "Prejudice",
      "Family Legacy", "Good vs Evil", "Man vs Nature", "Man vs Society"
    ],
    "Philosophical": [
      "Destiny vs Free Will", "Morality", "Truth vs Lies",
      "Knowledge & Ignorance", "Time", "Memory"
    ]
  };

  // Non-Fiction Themes
  const nonfictionThemeCategories = {
    "Personal Development": [
      "Self-Discovery", "Goal Setting", "Mindset Shift", "Overcoming Fear",
      "Building Confidence", "Finding Purpose", "Personal Transformation", "Habit Formation"
    ],
    "Financial": [
      "Wealth Building", "Debt Freedom", "Financial Independence", "Investing Basics",
      "Budgeting", "Generational Wealth", "Money Mindset", "Passive Income"
    ],
    "Business & Career": [
      "Leadership", "Entrepreneurship", "Career Growth", "Productivity",
      "Team Building", "Innovation", "Work-Life Balance", "Networking"
    ],
    "Health & Wellness": [
      "Mental Health", "Physical Fitness", "Nutrition", "Stress Management",
      "Mindfulness", "Sleep Optimization", "Holistic Health", "Self-Care"
    ],
    "Relationships": [
      "Communication", "Conflict Resolution", "Building Trust", "Boundaries",
      "Marriage", "Parenting", "Friendship", "Family Dynamics"
    ],
    "Spiritual & Faith": [
      "Faith Journey", "Prayer Life", "Spiritual Growth", "Finding Meaning",
      "Gratitude", "Forgiveness", "Service to Others", "Inner Peace"
    ]
  };

  // Fiction Tropes
  const fictionTropeCategories = {
    "Character Tropes": [
      "Chosen One", "Anti-Hero", "Mentor", "Reluctant Hero",
      "Tragic Hero", "Wise Old Man", "Femme Fatale", "Underdog",
      "Orphan Hero", "Rags to Riches", "Fish Out of Water"
    ],
    "Plot Tropes": [
      "Hero's Journey", "Redemption Arc", "Love Triangle",
      "Found Family", "Enemies to Lovers", "Forbidden Love",
      "Second Chance Romance", "Quest", "Mystery Box", "MacGuffin"
    ],
    "Fantasy/Sci-Fi": [
      "Time Travel", "Parallel Universe", "Prophecy",
      "Magic System", "Portal", "Ancient Artifact",
      "Dystopia", "Post-Apocalyptic", "Space Opera", "Cyberpunk"
    ],
    "Mystery/Thriller": [
      "Locked Room Mystery", "Unreliable Narrator", "Red Herring",
      "Whodunit", "Cat and Mouse", "Conspiracy", "Double Agent"
    ],
    "Romance": [
      "Meet Cute", "Fake Relationship", "Forced Proximity",
      "Secret Identity", "Star-Crossed Lovers", "Slow Burn",
      "Friends to Lovers", "Marriage of Convenience"
    ],
    "Horror": [
      "Haunted House", "Possession", "Body Horror", 
      "Psychological Horror", "Survival Horror", "Cosmic Horror"
    ]
  };

  // Non-Fiction Approaches/Frameworks (instead of tropes)
  const nonfictionApproachCategories = {
    "Self-Help Frameworks": [
      "Step-by-Step System", "7-Day Challenge", "30-Day Transformation",
      "Workbook Style", "Case Studies", "Q&A Format", "Journaling Prompts"
    ],
    "Finance Approaches": [
      "Baby Steps Method", "Pay Yourself First", "Envelope System",
      "FIRE Movement", "Wealth Ladder", "Money Audit", "Financial Detox"
    ],
    "Teaching Methods": [
      "Practical Exercises", "Real-World Examples", "Action Items",
      "Checklists", "Templates", "Before/After Stories", "Expert Interviews"
    ],
    "Counseling & Psychology": [
      "CBT Techniques", "Mindfulness Practices", "Inner Child Work",
      "Trauma-Informed", "Solution-Focused", "Strengths-Based", "Narrative Therapy"
    ],
    "Business Frameworks": [
      "Strategic Planning", "SWOT Analysis", "OKRs", "Lean Methodology",
      "Growth Hacking", "Customer Journey", "Systems Thinking"
    ],
    "Content Structure": [
      "Problem-Solution", "Myth-Busting", "Contrarian View",
      "Research-Backed", "Personal Story + Lessons", "Interview-Based"
    ]
  };

  // Use appropriate categories based on genre
  const themeCategories = isNonfiction ? nonfictionThemeCategories : fictionThemeCategories;
  const tropeCategories = isNonfiction ? nonfictionApproachCategories : fictionTropeCategories;

  const toggleTheme = (theme) => {
    const newThemes = selectedThemes.includes(theme)
      ? selectedThemes.filter(t => t !== theme)
      : [...selectedThemes, theme];
    onThemesChange(newThemes);
  };

  const toggleTrope = (trope) => {
    const newTropes = selectedTropes.includes(trope)
      ? selectedTropes.filter(t => t !== trope)
      : [...selectedTropes, trope];
    onTropesChange(newTropes);
  };

  const removeTheme = (theme) => {
    onThemesChange(selectedThemes.filter(t => t !== theme));
  };

  const removeTrope = (trope) => {
    onTropesChange(selectedTropes.filter(t => t !== trope));
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-base font-semibold">
          {isNonfiction ? "Topics & Approaches" : "Themes & Tropes"}
        </Label>
        <p className="text-xs text-stone-500 mt-1">
          {isNonfiction 
            ? "Select topics and approaches to guide your book content"
            : "Select themes and tropes to guide your story"
          }
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-stone-200">
        <button
          type="button"
          onClick={() => setActiveTab("themes")}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "themes"
              ? "text-amber-900 border-b-2 border-amber-900"
              : "text-stone-500 hover:text-stone-700"
          }`}
        >
          {isNonfiction ? "Topics" : "Themes"} ({selectedThemes.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("tropes")}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "tropes"
              ? "text-amber-900 border-b-2 border-amber-900"
              : "text-stone-500 hover:text-stone-700"
          }`}
        >
          {isNonfiction ? "Approaches" : "Tropes"} ({selectedTropes.length})
        </button>
      </div>

      {/* Selected Items Display */}
      {((activeTab === "themes" && selectedThemes.length > 0) || 
        (activeTab === "tropes" && selectedTropes.length > 0)) && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-xs font-semibold text-amber-900 mb-2">Selected:</p>
          <div className="flex flex-wrap gap-2">
            {activeTab === "themes" && selectedThemes.map(theme => (
              <Badge 
                key={theme}
                className="bg-amber-600 hover:bg-amber-700 cursor-pointer"
                onClick={() => removeTheme(theme)}
              >
                {theme}
                <X className="h-3 w-3 ml-1" />
              </Badge>
            ))}
            {activeTab === "tropes" && selectedTropes.map(trope => (
              <Badge 
                key={trope}
                className="bg-amber-600 hover:bg-amber-700 cursor-pointer"
                onClick={() => removeTrope(trope)}
              >
                {trope}
                <X className="h-3 w-3 ml-1" />
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Themes Content */}
      {activeTab === "themes" && (
        <div className="max-h-96 overflow-y-auto space-y-4 border border-stone-200 rounded-lg p-4 bg-stone-50">
          {Object.entries(themeCategories).map(([category, themes]) => (
            <div key={category}>
              <h4 className="font-semibold text-stone-900 mb-2 text-sm">{category}</h4>
              <div className="flex flex-wrap gap-2">
                {themes.map(theme => (
                  <button
                    key={theme}
                    type="button"
                    onClick={() => toggleTheme(theme)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                      selectedThemes.includes(theme)
                        ? "bg-amber-600 text-white font-medium shadow-md"
                        : "bg-white text-stone-700 border border-stone-300 hover:border-amber-500 hover:bg-amber-50"
                    }`}
                  >
                    {theme}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tropes Content */}
      {activeTab === "tropes" && (
        <div className="max-h-96 overflow-y-auto space-y-4 border border-stone-200 rounded-lg p-4 bg-stone-50">
          {Object.entries(tropeCategories).map(([category, tropes]) => (
            <div key={category}>
              <h4 className="font-semibold text-stone-900 mb-2 text-sm">{category}</h4>
              <div className="flex flex-wrap gap-2">
                {tropes.map(trope => (
                  <button
                    key={trope}
                    type="button"
                    onClick={() => toggleTrope(trope)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                      selectedTropes.includes(trope)
                        ? "bg-amber-600 text-white font-medium shadow-md"
                        : "bg-white text-stone-700 border border-stone-300 hover:border-amber-500 hover:bg-amber-50"
                    }`}
                  >
                    {trope}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TropesSelector;
